import { yupResolver } from "@hookform/resolvers/yup";
import { Grid } from "@material-ui/core";
import produce from "immer";
import { isUndefined, merge } from "lodash";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { DeepPartial, FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useImagePreview } from "../../../components/image/image-preview";
import { WrapWithStepperContext } from "../../../components/stepper/context";
import { StepInfo } from "../../../components/stepper/models";
import { VerticalLinearStepper } from "../../../components/stepper/vertical";
import { AppStore } from "../../../store";
import { AuthDTO } from "../../../store/auth/models/dto";
import { useFetchable } from "../../../store/common/fetchable";
import {
  Fetchable,
  isFetchableSuccess,
} from "../../../store/common/fetchable/helpers";
import { useDefaultFieldMap } from "../../../store/launch-configurations/models/default-fields";
import { LaunchConfigurationsDTO } from "../../../store/launch-configurations/models/dto";
import { Field } from "../../../store/launch-configurations/models/internal/field";
import { VerificationDTO } from "../../../store/verification/models/dto/kyc";
import { Verification } from "../../../store/verification/reducer";
import { SingleLaunchPage } from "../../token-onboarding/single";
import { StyledWidget } from "./common/components";
import { FormType } from "./common/form";
import { NewLaunchContextProvider } from "./context";
import { CryptoChains } from "./crypto-chains";
import { useNewLaunchConfigDefaults } from "./defaults";
import { Configuration } from "./general";
import { useParentFormSchema, useRemoveNulls } from "./hooks";
import { Other } from "./other";
import { Pages } from "./pages";
import { SecurityAndRestrictions } from "./security-restrictions";
import { Styling } from "./styling";

/**
 * This method will return a callback that takes the form current state and will filter
 * out the pages and fields for which the user has no access to due to him not being
 * subscribed to that service (e.g. Consent Note service) or not crypto fields
 * @param currentMerchantConfig
 * @returns
 */
const useFilterFieldsAndPages = (
  currentMerchantConfig: Fetchable<VerificationDTO.Responses.GetMerchantConfig.T>,
  isCrypto: boolean
) => {
  const defaultFieldMap = useDefaultFieldMap();

  return useCallback(
    (state: DeepPartial<LaunchConfigurationsDTO.Requests.SetSingle.T>) => {
      if (currentMerchantConfig.status !== "ready") return;
      state.pages?.forEach((page) => {
        if (page) {
          page.fields = page?.fields?.filter((f) => {
            if (isUndefined(f)) return false;
            if (Field.isDefaultField(f as Field.Field<string>)) {
              const { service, cryptoField } = defaultFieldMap[
                (f as Field.DefaultField).field
              ];
              if (!isCrypto && cryptoField) return false;
              if (isUndefined(service)) return true;
              return currentMerchantConfig.data.includedServices.includes(
                service
              );
            }
          });
        }
      });
      state.pages = state.pages?.filter((y) => (y?.fields?.length || 0) > 0);
    },
    [defaultFieldMap, currentMerchantConfig, isCrypto]
  );
};

export const NewConfigurationLaunchPage: FunctionComponent<{
  item?: LaunchConfigurationsDTO.Responses.GetSingle.T;
}> = ({ item }) => {
  const currentMerchantConfig = useFetchable(
    Verification.currentMerchantConfig
  );

  const newLaunchConfigDefaults = useNewLaunchConfigDefaults();

  /**
   * Remove nulls from item passed when updating an exiting launch
   */
  const processedItem = useRemoveNulls(
    item
  ) as LaunchConfigurationsDTO.Responses.GetSingle.T;
  const updateId = useMemo(() => processedItem?.id, [processedItem]);

  const schema = useParentFormSchema();
  const defaultFieldMap = useDefaultFieldMap();

  const [isCrypto, setIsCrypto] = useState<boolean>(
    () =>
      !!processedItem &&
      (processedItem.configuration.participationServices.onchainAnalysis
        .enabled ||
        !!processedItem.pages.find((p) =>
          p.fields.find(
            (f) =>
              Field.isDefaultField(f) && defaultFieldMap[f.field]?.cryptoField
          )
        ))
  );

  const defaultValues = useMemo(
    () => processedItem ?? newLaunchConfigDefaults,
    [newLaunchConfigDefaults, processedItem]
  );

  const form = useForm<FormType>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const [values, setValues] = useState<DeepPartial<FormType>>();

  const selectedLanguages = form.watch("configuration.supportedFormLanguages");

  const steps: Array<
    StepInfo & {
      content:
        | typeof Other
        | typeof Configuration
        | typeof Styling
        | typeof CryptoChains
        // | typeof Pages
        | typeof SecurityAndRestrictions;
    } & {
      cryptoOnly?: boolean;
    }
  > = useMemo(
    () =>
      [
        {
          title: "General",
          content: Configuration,
        },
        {
          title: "Styling",
          content: Styling,
        },
        {
          title: "Security and Restrictions",
          content: SecurityAndRestrictions,
        },
        {
          title: "Other",
          content: Other,
        },
        {
          title: "Crypto Chains",
          content: CryptoChains,
          cryptoOnly: true,
        },
        {
          title: "Pages",
          content: Pages,
          cryptoOnly: false,
        },
      ].filter((x) => isCrypto || !x.cryptoOnly),
    [isCrypto]
  );

  const filterFieldsAndPages = useFilterFieldsAndPages(
    currentMerchantConfig,
    isCrypto
  );

  const currentAuth = useSelector<
    AppStore,
    Fetchable<AuthDTO.Responses.Login.T>
  >((store) => store.auth.login);

  // Get header image string to preview
  const imagePreview = useImagePreview(form.watch("headerImage"));

  // When crypto is toggled, add by default source wallet, destination wallet and pledge fields
  useEffect(() => {
    // only reset pages

    if (isFetchableSuccess(currentMerchantConfig) && !processedItem) {
      form.setValue(
        "pages",
        produce<DeepPartial<LaunchConfigurationsDTO.Requests.SetSingle.T>>(
          newLaunchConfigDefaults,
          filterFieldsAndPages
        ).pages as FormType["pages"]
      );
    }
  }, [
    isCrypto,
    form.setValue,
    newLaunchConfigDefaults,
    filterFieldsAndPages,
    processedItem,
    currentMerchantConfig,
  ]);

  /**
   * Do an initial load of the form (form.reset) by filtering out the necessary fields/pages
   * depending on the user's subscribed services and assign the merchant ID if we are logged
   * in as a merchant
   */
  useEffect(() => {
    const newVals: DeepPartial<FormType> = {};
    merge(
      newVals,

      // add only allowed fields
      isFetchableSuccess(currentMerchantConfig)
        ? produce<DeepPartial<LaunchConfigurationsDTO.Requests.SetSingle.T>>(
            processedItem ?? newLaunchConfigDefaults,
            filterFieldsAndPages
          )
        : processedItem ?? newLaunchConfigDefaults,

      // add the merchant ID
      !processedItem &&
        isFetchableSuccess(currentAuth) &&
        currentAuth.data.accessToken.roles.includes("ROLE_MERCHANT")
        ? {
            configuration: {
              merchantConfig: {
                merchantId: currentAuth.data.accessToken.credentialsId, // If the user is merchant, add merchant ID to the config
              },
            },
          }
        : {}
    );

    form.reset(newVals);
    setValues(newVals);
  }, [
    currentMerchantConfig,
    form,
    currentAuth,
    processedItem,
    newLaunchConfigDefaults,
    filterFieldsAndPages,
    setValues,
  ]);

  useEffect(() => {
    const sub = form.watch(setValues);
    return () => sub.unsubscribe();
  }, [form.watch, setValues]);

  // Merge the form values to the imagePreview
  const aggregatedValues: DeepPartial<FormType> = useMemo(() => {
    const newVals: DeepPartial<FormType> = {};
    merge(
      newVals,
      values,
      { id: "random" },
      { configuration: { header: { imageUrl: imagePreview } } }
    );

    // Remove dates otherwise preview is unavailable
    delete newVals?.configuration?.between;

    return newVals;
  }, [values, imagePreview]);

  return (
    <NewLaunchContextProvider
      languages={selectedLanguages}
      isCrypto={isCrypto}
      setIsCrypto={setIsCrypto}
    >
      <FormProvider {...form}>
        <WrapWithStepperContext>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <VerticalLinearStepper
                steps={steps}
                stepsProps={useMemo(
                  () => ({
                    updateId,
                  }),
                  [updateId]
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <StyledWidget title="Preview">
                {values ? (
                  <SingleLaunchPage item={aggregatedValues as any} /> // TODO check if partial suffices and fix typing
                ) : null}
              </StyledWidget>
            </Grid>
          </Grid>
        </WrapWithStepperContext>
      </FormProvider>
    </NewLaunchContextProvider>
  );
};
